/**
 * CustomMoodBuilder Component
 *
 * Allows users to create and edit custom mood configurations with:
 * - Mood name and emoji
 * - Audio feature sliders (energy, valence, danceability, etc.)
 * - Genre tags
 * - Seed tracks/artists
 */
import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '@/lib/auth';

interface AudioFeatureRanges {
  energy_min?: number;
  energy_max?: number;
  valence_min?: number;
  valence_max?: number;
  danceability_min?: number;
  danceability_max?: number;
  acousticness_min?: number;
  acousticness_max?: number;
  instrumentalness_min?: number;
  instrumentalness_max?: number;
  tempo_min?: number;
  tempo_max?: number;
}

interface CustomMood {
  id?: number;
  name: string;
  emoji?: string;
  description?: string;
  audio_features?: AudioFeatureRanges;
  genre_tags?: string[];
  seed_track_ids?: string[];
  seed_artist_ids?: string[];
  is_active?: boolean;
}

interface CustomMoodBuilderProps {
  moodId?: number;
  onSave?: () => void;
  onCancel?: () => void;
}

interface AudioFeatureConfig {
  key: keyof AudioFeatureRanges;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
}

const AUDIO_FEATURES: AudioFeatureConfig[] = [
  { key: 'energy_min', label: 'Energy Min', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'energy_max', label: 'Energy Max', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'valence_min', label: 'Valence Min (Mood)', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'valence_max', label: 'Valence Max (Mood)', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'danceability_min', label: 'Danceability Min', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'danceability_max', label: 'Danceability Max', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'acousticness_min', label: 'Acousticness Min', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'acousticness_max', label: 'Acousticness Max', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'instrumentalness_min', label: 'Instrumentalness Min', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'instrumentalness_max', label: 'Instrumentalness Max', min: 0, max: 1, step: 0.05, unit: '' },
  { key: 'tempo_min', label: 'Tempo Min (BPM)', min: 0, max: 300, step: 5, unit: 'BPM' },
  { key: 'tempo_max', label: 'Tempo Max (BPM)', min: 0, max: 300, step: 5, unit: 'BPM' },
];

const CustomMoodBuilder: React.FC<CustomMoodBuilderProps> = ({ moodId, onSave, onCancel }) => {
  const [mood, setMood] = useState<CustomMood>({
    name: '',
    emoji: '',
    description: '',
    audio_features: {},
    genre_tags: [],
    seed_track_ids: [],
    seed_artist_ids: [],
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genreInput, setGenreInput] = useState('');

  useEffect(() => {
    if (moodId) {
      loadMood(moodId);
    }
  }, [moodId]);

  const loadMood = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/moods/${id}`);
      if (!response.ok) throw new Error('Failed to load mood');
      const data = await response.json();
      setMood(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load mood');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = moodId ? `/api/moods/${moodId}` : '/api/moods';
      const method = moodId ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mood),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save mood');
      }

      onSave?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mood');
    } finally {
      setLoading(false);
    }
  };

  const updateAudioFeature = (key: keyof AudioFeatureRanges, value: number) => {
    setMood(prev => ({
      ...prev,
      audio_features: {
        ...prev.audio_features,
        [key]: value,
      },
    }));
  };

  const addGenreTag = () => {
    if (!genreInput.trim()) return;
    if (mood.genre_tags && mood.genre_tags.length >= 20) {
      setError('Maximum 20 genre tags allowed');
      return;
    }

    const newTag = genreInput.trim();
    if (mood.genre_tags?.includes(newTag)) {
      setError('Genre tag already added');
      return;
    }

    setMood(prev => ({
      ...prev,
      genre_tags: [...(prev.genre_tags || []), newTag],
    }));
    setGenreInput('');
    setError(null);
  };

  const removeGenreTag = (tag: string) => {
    setMood(prev => ({
      ...prev,
      genre_tags: prev.genre_tags?.filter(t => t !== tag),
    }));
  };

  if (loading && moodId) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading mood...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">
          {moodId ? 'Edit Custom Mood' : 'Create Custom Mood'}
        </h2>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded p-3 text-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Mood Name *
          </label>
          <input
            type="text"
            value={mood.name}
            onChange={(e) => setMood({ ...mood, name: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-spotify-green"
            placeholder="e.g., Deep Focus, Night Drive, Workout Beast"
            maxLength={100}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Emoji
            </label>
            <input
              type="text"
              value={mood.emoji || ''}
              onChange={(e) => setMood({ ...mood, emoji: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-spotify-green text-2xl text-center"
              placeholder="🎵"
              maxLength={10}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Status
            </label>
            <select
              value={mood.is_active ? 'active' : 'inactive'}
              onChange={(e) => setMood({ ...mood, is_active: e.target.value === 'active' })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-spotify-green"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={mood.description || ''}
            onChange={(e) => setMood({ ...mood, description: e.target.value })}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-spotify-green"
            placeholder="Describe the vibe, energy, or style of this mood..."
            rows={3}
            maxLength={500}
          />
        </div>
      </div>

      {/* Audio Features */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
          Audio Features
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {AUDIO_FEATURES.map((feature) => (
            <div key={feature.key}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {feature.label}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={feature.min}
                  max={feature.max}
                  step={feature.step}
                  value={mood.audio_features?.[feature.key] ?? feature.min}
                  onChange={(e) => updateAudioFeature(feature.key, parseFloat(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm text-gray-400 w-16 text-right">
                  {mood.audio_features?.[feature.key]?.toFixed(feature.max <= 1 ? 2 : 0) ?? feature.min}
                  {feature.unit && ` ${feature.unit}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Genre Tags */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
          Genre Tags ({mood.genre_tags?.length || 0}/20)
        </h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={genreInput}
            onChange={(e) => setGenreInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addGenreTag()}
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-spotify-green"
            placeholder="e.g., electronic, rock, jazz"
            maxLength={50}
          />
          <button
            onClick={addGenreTag}
            className="bg-spotify-green hover:bg-spotify-green-dark text-black font-semibold px-4 py-2 rounded transition-colors"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {mood.genre_tags?.map((tag) => (
            <span
              key={tag}
              className="bg-gray-800 border border-gray-700 rounded-full px-3 py-1 text-sm text-white flex items-center gap-2"
            >
              {tag}
              <button
                onClick={() => removeGenreTag(tag)}
                className="text-gray-400 hover:text-red-400 transition-colors"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t border-gray-700">
        <button
          onClick={handleSave}
          disabled={loading || !mood.name.trim()}
          className="flex-1 bg-spotify-green hover:bg-spotify-green-dark disabled:bg-gray-700 disabled:text-gray-500 text-black font-semibold px-4 py-2 rounded transition-colors"
        >
          {loading ? 'Saving...' : moodId ? 'Update Mood' : 'Create Mood'}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-6 bg-gray-800 hover:bg-gray-700 text-white font-semibold py-2 rounded transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomMoodBuilder;
